import { rotatedSize, type Size } from "./geometry";
import { MARGIN_MM, type DetectedRegion } from "./detection";
import type { BrickRegion, BrickType, MasterImage, Rect, Stroke } from "./types";

export const MIN_REGION_SIZE = 4;
// The range api/utils/masterImageRules.js accepts; a stroke outside it fails every autosave.
const MIN_STROKE_RADIUS = 0.5;
const MAX_STROKE_RADIUS = 1000;

const HISTORY_LIMIT = 100;

export interface DraftState {
  masterImage: MasterImage;
  selection: string[];
  past: MasterImage[];
  future: MasterImage[];
  gesture: string | null;
}

interface Edges {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
}

export type DraftAction =
  | { type: "select"; ids: string[]; additive?: boolean }
  | { type: "add"; region: BrickRegion }
  | { type: "move"; dx: number; dy: number; gesture?: string }
  | ({ type: "resize"; gesture?: string } & Edges)
  | { type: "delete" }
  | { type: "flipType" }
  | { type: "setType"; brickType: BrickType }
  | { type: "setTolerance"; tolerance: number }
  | { type: "setRegionTolerance"; tolerance: number | null }
  | { type: "addStroke"; id: string; stroke: Stroke }
  | { type: "clearStrokes" }
  | { type: "undo" }
  | { type: "redo" }
  | {
      type: "redetect";
      rotation: number;
      brickWidthMm: number;
      brickHeightMm: number;
      pxPerMm: number | null;
      regions: BrickRegion[];
    };

export const initDraft = (masterImage: MasterImage): DraftState => ({
  masterImage,
  selection: [],
  past: [],
  future: [],
  gesture: null,
});

export const canUndo = (state: DraftState) => state.past.length > 0;

export const canRedo = (state: DraftState) => state.future.length > 0;

export const regionFromDetected = (detected: DetectedRegion, id: string): BrickRegion => ({
  id,
  x: detected.x,
  y: detected.y,
  width: detected.width,
  height: detected.height,
  type: detected.type,
  tolerance: null,
  strokes: [],
  edited: false,
});

export const editedRegionCount = (regions: BrickRegion[]) => regions.filter((r) => r.edited).length;

export const draftPayload = ({ rotation, tolerance, brickWidthMm, brickHeightMm, pxPerMm, regions }: MasterImage) => ({
  rotation,
  tolerance,
  brickWidthMm,
  brickHeightMm,
  pxPerMm,
  regions,
});

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const fitRect = (rect: Rect, bounds: Size): Rect => {
  const width = clamp(Math.round(rect.width), MIN_REGION_SIZE, bounds.width);
  const height = clamp(Math.round(rect.height), MIN_REGION_SIZE, bounds.height);
  return {
    x: clamp(Math.round(rect.x), 0, bounds.width - width),
    y: clamp(Math.round(rect.y), 0, bounds.height - height),
    width,
    height,
  };
};

const resizeRect = (r: Rect, edges: Edges, bounds: Size): Rect => {
  let x0 = clamp(r.x + (edges.left ?? 0), 0, bounds.width);
  let x1 = clamp(r.x + r.width + (edges.right ?? 0), 0, bounds.width);
  let y0 = clamp(r.y + (edges.top ?? 0), 0, bounds.height);
  let y1 = clamp(r.y + r.height + (edges.bottom ?? 0), 0, bounds.height);
  if (edges.left !== undefined) x0 = Math.min(x0, x1 - MIN_REGION_SIZE);
  else x1 = Math.max(x1, x0 + MIN_REGION_SIZE);
  if (edges.top !== undefined) y0 = Math.min(y0, y1 - MIN_REGION_SIZE);
  else y1 = Math.max(y1, y0 + MIN_REGION_SIZE);
  return {
    x: Math.round(x0),
    y: Math.round(y0),
    width: Math.round(x1 - x0),
    height: Math.round(y1 - y0),
  };
};

const applyEdit = (state: DraftState, action: DraftAction): DraftState => {
  const { masterImage, selection } = state;
  const bounds = rotatedSize(masterImage.width, masterImage.height, masterImage.rotation);
  const selected = new Set(selection);
  const withRegions = (regions: BrickRegion[]): DraftState => ({
    ...state,
    masterImage: { ...masterImage, regions },
  });
  const updateSelected = (update: (r: BrickRegion) => BrickRegion): DraftState =>
    selection.length === 0
      ? state
      : withRegions(
          masterImage.regions.map((r) => (selected.has(r.id) ? { ...update(r), edited: true } : r))
        );

  switch (action.type) {
    case "select": {
      if (!action.additive) return { ...state, selection: action.ids };
      const next = new Set(selection);
      for (const id of action.ids) {
        if (next.has(id)) next.delete(id);
        else next.add(id);
      }
      return { ...state, selection: [...next] };
    }
    case "add": {
      const region = { ...action.region, ...fitRect(action.region, bounds), edited: true };
      const pxPerMm =
        masterImage.pxPerMm == null && region.type === "full" && masterImage.brickHeightMm != null
          ? region.height / (masterImage.brickHeightMm + 2 * MARGIN_MM)
          : masterImage.pxPerMm;
      return {
        ...state,
        masterImage: { ...masterImage, pxPerMm, regions: [...masterImage.regions, region] },
        selection: [region.id],
      };
    }
    case "move": {
      const moving = masterImage.regions.filter((r) => selected.has(r.id));
      if (moving.length === 0) return state;
      const dx = clamp(
        Math.round(action.dx),
        -Math.min(...moving.map((r) => r.x)),
        Math.min(...moving.map((r) => bounds.width - r.x - r.width))
      );
      const dy = clamp(
        Math.round(action.dy),
        -Math.min(...moving.map((r) => r.y)),
        Math.min(...moving.map((r) => bounds.height - r.y - r.height))
      );
      if (dx === 0 && dy === 0) return state;
      return updateSelected((r) => ({ ...r, x: r.x + dx, y: r.y + dy }));
    }
    case "resize":
      return updateSelected((r) => ({ ...r, ...resizeRect(r, action, bounds) }));
    case "delete":
      return selection.length === 0
        ? state
        : {
            ...state,
            masterImage: { ...masterImage, regions: masterImage.regions.filter((r) => !selected.has(r.id)) },
            selection: [],
          };
    case "flipType":
      return updateSelected((r) => ({ ...r, type: r.type === "full" ? "half" : "full" }));
    case "setType":
      return masterImage.regions.every((r) => !selected.has(r.id) || r.type === action.brickType)
        ? state
        : updateSelected((r) => ({ ...r, type: action.brickType }));
    case "clearStrokes":
      return masterImage.regions.every((r) => !selected.has(r.id) || r.strokes.length === 0)
        ? state
        : updateSelected((r) => ({ ...r, strokes: [] }));
    case "setTolerance":
      return { ...state, masterImage: { ...masterImage, tolerance: action.tolerance } };
    case "setRegionTolerance":
      return updateSelected((r) => ({ ...r, tolerance: action.tolerance }));
    case "addStroke": {
      if (!masterImage.regions.some((r) => r.id === action.id)) return state;
      const stroke = {
        ...action.stroke,
        radius: clamp(action.stroke.radius, MIN_STROKE_RADIUS, MAX_STROKE_RADIUS),
      };
      return withRegions(
        masterImage.regions.map((r) =>
          r.id === action.id ? { ...r, strokes: [...r.strokes, stroke], edited: true } : r
        )
      );
    }
    case "redetect": {
      const nextBounds = rotatedSize(masterImage.width, masterImage.height, action.rotation);
      return {
        ...state,
        selection: [],
        masterImage: {
          ...masterImage,
          rotation: action.rotation,
          brickWidthMm: action.brickWidthMm,
          brickHeightMm: action.brickHeightMm,
          pxPerMm: action.pxPerMm,
          regions: action.regions.map((r) => ({ ...r, ...fitRect(r, nextBounds) })),
        },
      };
    }
    default:
      return state;
  }
};

const restore = (state: DraftState, masterImage: MasterImage, past: MasterImage[], future: MasterImage[]) => {
  const existing = new Set(masterImage.regions.map((r) => r.id));
  return {
    masterImage,
    past,
    future,
    selection: state.selection.filter((id) => existing.has(id)),
    gesture: null,
  };
};

export const draftReducer = (state: DraftState, action: DraftAction): DraftState => {
  if (action.type === "undo") {
    if (state.past.length === 0) return state;
    return restore(state, state.past[state.past.length - 1], state.past.slice(0, -1), [
      state.masterImage,
      ...state.future,
    ]);
  }
  if (action.type === "redo") {
    if (state.future.length === 0) return state;
    return restore(state, state.future[0], [...state.past, state.masterImage], state.future.slice(1));
  }
  const next = applyEdit(state, action);
  if (next.masterImage === state.masterImage) return next;
  const gesture = "gesture" in action ? action.gesture ?? null : null;
  const continuesGesture = gesture != null && gesture === state.gesture;
  return {
    ...next,
    past: continuesGesture ? state.past : [...state.past, state.masterImage].slice(-HISTORY_LIMIT),
    future: [],
    gesture,
  };
};
